import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutDashboard, Users, FileText, UserPlus, Target } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import PortalLayout from '../../components/PortalLayout';
import axios from 'axios';

const PortalDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const { token } = useAuth();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      fetchNewLeads();
    }
  }, [user]);

  const fetchNewLeads = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/portal/leads/inbound`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newLeads = response.data.leads.filter(l => l.status === 'new');
      setNewLeadsCount(newLeads.length);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const adminCards = [
    { title: 'User Management', desc: 'Manage users and permissions', icon: Users, link: '/portal/admin/users', color: 'from-blue-500 to-cyan-500' },
    { title: 'Access Requests', desc: 'Review and approve requests', icon: UserPlus, link: '/portal/admin/access-requests', color: 'from-purple-500 to-pink-500' },
    { title: 'Blog Management', desc: 'Create and manage blog posts', icon: FileText, link: '/portal/admin/blog', color: 'from-green-500 to-emerald-500' }
  ];

  return (
    <PortalLayout>
      {/* Gradient Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-600 via-sky-600 to-purple-600 py-12">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Welcome back, {user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : 'User'}!</h1>
            <p className="text-blue-100 text-lg">{user?.first_name} {user?.last_name}</p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        {(user?.role === 'admin' || user?.role === 'super_admin') ? (
          <>
            {/* New Leads KPI */}
            {newLeadsCount > 0 && (
              <div className="mb-6">
                <Link to="/portal/admin/leads?status=new">
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer">
                    <div className="flex items-center justify-between text-white">
                      <div>
                        <p className="text-sm font-semibold mb-1">New Inbound Leads</p>
                        <p className="text-3xl font-bold">{newLeadsCount}</p>
                      </div>
                      <Target className="w-12 h-12 opacity-80" />
                    </div>
                    <p className="text-orange-100 text-sm mt-2">Click to view new leads →</p>
                  </motion.div>
                </Link>
              </div>
            )}

            {/* Admin Cards */}
            <div className="grid md:grid-cols-3 gap-6">
            {adminCards.map((card, idx) => (
              <Link key={idx} to={card.link}>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.1 }} className="group p-8 bg-white rounded-xl border border-gray-200 hover:border-sky-300 hover:shadow-xl transition-all cursor-pointer">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                    <card.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-sky-600 transition-colors">{card.title}</h3>
                  <p className="text-gray-600 text-sm">{card.desc}</p>
                  <div className="mt-4 inline-flex items-center text-blue-600 text-sm font-medium">Access Now →</div>
                </motion.div>
              </Link>
            ))}
          </div>
          </>
        ) : (
          <div className="bg-white rounded-xl p-8 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to the Portal</h2>
            <p className="text-gray-600">Your account has been created. Additional features will be available based on your role.</p>
          </div>
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalDashboard;
